import axios from 'axios';
import { store } from '../store';
import { logout, setAccessToken } from '../store/slices/authSlice';

// 假设后端运行在 localhost:3000
const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshRequest: Promise<string> | null = null;

// 请求拦截器：添加 Token
api.interceptors.request.use(
  (config) => {
    const token = store.getState().auth.token;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：处理 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as
      | ({ _retry?: boolean; url?: string; headers?: Record<string, string> })
      | undefined;

    if (!error.response || error.response.status !== 401 || !originalRequest) {
      return Promise.reject(error);
    }

    const requestUrl = originalRequest.url || '';
    const isAuthRequest =
      requestUrl.includes('/auth/login') ||
      requestUrl.includes('/auth/teacher-login') ||
      requestUrl.includes('/auth/student-login') ||
      requestUrl.includes('/auth/refresh');

    if (originalRequest._retry || isAuthRequest) {
      store.dispatch(logout());
      return Promise.reject(error);
    }

    const { refreshToken } = store.getState().auth;

    if (!refreshToken) {
      store.dispatch(logout());
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      if (!refreshRequest) {
        refreshRequest = axios
          .post(`${API_URL}/auth/refresh`, { refreshToken })
          .then((response) => {
            const nextAccessToken = response.data?.accessToken;

            if (typeof nextAccessToken !== 'string' || !nextAccessToken) {
              throw new Error('Missing access token');
            }

            store.dispatch(setAccessToken(nextAccessToken));
            return nextAccessToken;
          })
          .finally(() => {
            refreshRequest = null;
          });
      }

      const nextAccessToken = await refreshRequest;
      originalRequest.headers = originalRequest.headers ?? {};
      originalRequest.headers.Authorization = `Bearer ${nextAccessToken}`;
      return api.request(originalRequest);
    } catch (refreshError) {
      store.dispatch(logout());
      return Promise.reject(refreshError);
    }

    return Promise.reject(error);
  }
);

export default api;
